import { useCallback, useEffect, useRef } from "react";

import { getNextRevealChunk } from "./revealText";

const TICK_MS = 35;

type Update = (id: string, text: string) => void;

export type TypewriterApi = {
  /** Set or extend the target text for `id`. New chunks reveal at TICK_MS each. */
  setTarget: (id: string, text: string) => void;
  /** Drop tracking for a single id (does not clear the on-screen text). */
  reset: (id: string) => void;
  /** Stop the timer and forget every target. */
  cancelAll: () => void;
};

/**
 * Word-by-word typewriter that decouples LLM stream jitter from display speed.
 * Caller owns the rendered text; this hook tells the caller (via `onUpdate`)
 * which id has new text to show.
 */
export const useTypewriter = (onUpdate: Update): TypewriterApi => {
  const targets = useRef<Record<string, string>>({});
  const displayed = useRef<Record<string, string>>({});
  const timer = useRef<number>();

  const clearTimer = useCallback(() => {
    if (timer.current !== undefined) {
      window.clearTimeout(timer.current);
      timer.current = undefined;
    }
  }, []);

  const tick = useCallback(() => {
    timer.current = undefined;
    const pending = Object.entries(targets.current).find(
      ([id, target]) => (displayed.current[id] ?? "").length < target.length
    );
    if (!pending) return;

    const [id, target] = pending;
    const current = displayed.current[id] ?? "";
    const next = current + getNextRevealChunk(current, target);
    displayed.current[id] = next;
    if (next.length >= target.length) delete targets.current[id];

    onUpdate(id, next);

    if (Object.keys(targets.current).length > 0) schedule();
  }, [onUpdate]);

  const schedule = useCallback(() => {
    if (timer.current !== undefined) return;
    timer.current = window.setTimeout(tick, TICK_MS);
  }, [tick]);

  const setTarget = useCallback<TypewriterApi["setTarget"]>(
    (id, text) => {
      const current = displayed.current[id] ?? "";
      if (!text.startsWith(current)) {
        displayed.current[id] = "";
        onUpdate(id, "");
      }
      targets.current[id] = text;
      schedule();
    },
    [onUpdate, schedule]
  );

  const reset = useCallback<TypewriterApi["reset"]>((id) => {
    delete targets.current[id];
    delete displayed.current[id];
  }, []);

  const cancelAll = useCallback<TypewriterApi["cancelAll"]>(() => {
    clearTimer();
    targets.current = {};
    displayed.current = {};
  }, [clearTimer]);

  useEffect(() => clearTimer, [clearTimer]);

  return { cancelAll, reset, setTarget };
};
